// Engineer PWA "My timesheet" tab (Session AEL, redesigned Session AFF, travel logging added
// Session ANF and MOVED OFF THIS SCREEN in Session ANT). Read only apart from one action: the
// engineer may SUBMIT their own week for approval. Every entry's time, type and note is the
// office's to set, and there is no edit control on this screen.
//
// WHERE THE TRAVEL CONTROL WENT. Session ANF put a "Log travel" button here, opening a bottom sheet
// that took a day and a number of minutes and wrote a TRAVEL entry with NO job attached. That was
// the wrong shape: travel is a fact about a JOURNEY TO A JOB, which is how the field-service
// products ELE is measured against capture it and how ELE itself actually works ("logging on to a
// job... how long did it take you to get there"). Session ANT moved the question onto the job sheet,
// beside the arrival time, and the entry now reaches this screen the same way the on-site hours do:
// generated at office approval, carrying the job it belongs to. The button, its bottom sheet, its
// two API routes and the per-row Remove control are GONE rather than hidden, because a second way to
// log the same drive is a week that can silently double.
//
// NOTHING HERE IS QUEUED OFFLINE, and that is stated rather than assumed. There is no timesheet
// outbox: src/lib/offline/timesheetStore.ts says in terms that "submitting goes straight to the
// server and is never queued or cached", and the three outboxes that do exist (job completions,
// photos, messages) are three hand-written mechanisms, not one generic queue. So Submit is gated on
// being online and says why. Travel no longer needs that gate at all: it rides the job completion,
// which DOES have an outbox, so a figure typed in a basement is held and sent on reconnect.
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { ApiError, api } from "../../lib/api";
import type { TimesheetSheet, TimesheetStatus, TimesheetEntry, TimesheetPendingJob, TimesheetUnrecordedJob } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { hasOwnEngineerRecords, isOversightAccount } from "../../lib/pwaIdentity";
import { presentableServerMessage } from "../../lib/serverMessage";
import { OversightNotice } from "./OversightNotice";
import { useEngineerData } from "./EngineerDataContext";
import { loadTimesheet, clearTimesheetCache } from "../../lib/offline/timesheetStore";
import { formatDurationHm } from "../../lib/timeOnSite";
import { jobTypeLabel } from "../../lib/jobType";
import { TimesheetBreakdown } from "../../components/TimesheetBreakdown";

// The Monday date string N weeks away from the given Monday (YYYY-MM-DD, UTC). Mirrors the office
// Timesheets page's own shiftWeek (src/routes/office/TimesheetsPage.tsx): same week arithmetic,
// kept local here since that file is out of this session's scope to import from.
function shiftWeek(weekStart: string, deltaWeeks: number): string {
  const d = new Date(`${weekStart}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaWeeks * 7);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// A plain YYYY-MM-DD as a compact "Mon 13 Jul". UTC, since the value carries no time component and
// a local parse could roll it onto the wrong day.
function entryDateLabel(dateOnly: string): string {
  const d = new Date(`${dateOnly}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dateOnly;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
}

function fullDateLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

// A just-submitted stamp, in the device's own local time: "13 July 2026 at 14:05".
function dateTimeStamp(d: Date): string {
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date} at ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_PILL: Record<TimesheetStatus, { label: string; className: string }> = {
  DRAFT: { label: "Draft", className: "border-ele-muted text-ele-muted" },
  SUBMITTED: { label: "Submitted", className: "border-[#2CB9F3] text-[var(--ele-loler-ink)]" },
  APPROVED: { label: "Approved", className: "border-[#63C41F] text-[var(--ele-green-ink)]" },
};

function StatusPill({ status }: { status: TimesheetStatus }) {
  const { label, className } = STATUS_PILL[status];
  return <span className={`inline-block rounded-full border px-3 py-1 font-menu text-xs uppercase tracking-wider ${className}`}>{label}</span>;
}

// Shown whenever the screen is serving cached data that is past its lifetime because the network
// could not be reached. Old figures beat a blank screen underground, but never silently.
function StaleNotice() {
  return (
    <p className="mb-3 rounded-lg border border-ele-card-border bg-ele-card px-3 py-2 text-xs text-ele-muted">
      Showing saved information. It will update when you are back online.
    </p>
  );
}

// Session ANT: the per-row two-step Remove control that stood here was the delete half of the
// engineer's own travel log, and went with it. Nothing on this screen is the engineer's to remove
// any more: a travel entry is generated from the job sheet at office approval, so correcting one
// means correcting the sheet or asking the office, exactly as every other entry already did.
function EntryRow({ entry }: { entry: TimesheetEntry }) {
  const noTimes = entry.minutes == null;
  const site = entry.siteName?.trim() || entry.typeLabel;
  // THE PILL FOLLOWS THE ENTRY TYPE UNLESS THE ENTRY IS ON_SITE, which is the identical rule the
  // server's own summariseByType applies to bucket these minutes (D31: travel is never folded into
  // on-site time). Session ANT made this load-bearing: a TRAVEL entry now carries the job it belongs
  // to, so the previous "jobType wins" line would have printed the drive to Sopwell House as
  // "Service", directly under the real Service row for the same visit, and the engineer would have
  // read one visit as two.
  const pill = entry.entryType === "ON_SITE" && entry.jobType ? jobTypeLabel(entry.jobType) : entry.typeLabel;
  return (
    <li className="flex items-start justify-between gap-3 border-t border-ele-card-border px-4 py-3 first:border-t-0">
      <div className="min-w-0">
        <p className="text-xs text-ele-muted">{entryDateLabel(entry.date)}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-ele-strong">{site}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-ele-card-border px-2 py-0.5 text-[11px] uppercase tracking-wide text-ele-muted">{pill}</span>
          {entry.eleNumber && <span className="text-[11px] text-[var(--ele-loler-ink)]">{entry.eleNumber}</span>}
        </div>
        {noTimes && (
          <span className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--ele-amber-text-deep)]">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> No times recorded
          </span>
        )}
      </div>
      <div className="flex flex-none flex-col items-end gap-1">
        <span className="text-sm text-ele-strong">{noTimes ? <span className="text-ele-muted">-</span> : formatDurationHm(entry.minutes)}</span>
      </div>
    </li>
  );
}

function PendingRow({ job }: { job: TimesheetPendingJob }) {
  const site = job.siteName?.trim() || jobTypeLabel(job.jobType);
  const noTimes = job.durationMins == null;
  return (
    <li className="flex items-start justify-between gap-3 border-t border-ele-card-border px-4 py-3 opacity-60 first:border-t-0">
      <div className="min-w-0">
        <p className="text-xs text-ele-muted">{entryDateLabel(job.date)}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-ele-strong">{site}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-[#b8860b] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[var(--ele-amber-text-deep)]">Awaiting approval</span>
          {job.eleNumber && <span className="text-[11px] text-[var(--ele-loler-ink)]">{job.eleNumber}</span>}
        </div>
      </div>
      <div className="flex-none text-right text-sm text-ele-muted">{noTimes ? "-" : formatDurationHm(job.durationMins)}</div>
    </li>
  );
}

// A completed visit whose hours reached no timesheet (Session ANF). Deliberately styled apart from
// PendingRow above, because the two mean opposite things and reading one as the other would be
// worse than showing neither: a pending visit ARRIVES on its own once the office approves it, and
// this one never will until somebody records the times. Amber (the platform's advisory colour)
// rather than the pending row's faded treatment, and it carries no duration column at all, since
// the whole point is that there is no time to show.
function UnrecordedRow({ job }: { job: TimesheetUnrecordedJob }) {
  const site = job.siteName?.trim() || jobTypeLabel(job.jobType);
  return (
    <li className="flex items-start justify-between gap-3 border-t border-ele-card-border px-4 py-3 first:border-t-0">
      <div className="min-w-0">
        <p className="text-xs text-ele-muted">{entryDateLabel(job.date)}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-ele-strong">{site}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-[#b8860b] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[var(--ele-amber-text-deep)]">No time recorded</span>
          {job.eleNumber && <span className="text-[11px] text-[var(--ele-loler-ink)]">{job.eleNumber}</span>}
        </div>
      </div>
    </li>
  );
}

export function MyTimesheet() {
  const { online } = useEngineerData();
  const { user } = useAuth();
  // An oversight account has no timesheet of its own, so asking the server for one can only be
  // refused. Decide it here rather than firing a doomed request and rendering its refusal as a
  // failure (Session AGV). See src/lib/pwaIdentity.ts.
  const ownRecords = hasOwnEngineerRecords(user);
  // undefined = the current week (the server resolves it); a YYYY-MM-DD steps to a specific week.
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [data, setData] = useState<TimesheetSheet | null>(null);
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);
  // The REASON the load failed, not just that it did. It used to be a bare boolean, so a 400
  // carrying a plain-English explanation, a 500 and a dropped connection all rendered the same
  // "Could not load your timesheet just now" behind a Try again that could not help (Session AGV).
  const [error, setError] = useState<{ message: string | null; code: string | null; retryable: boolean } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<{ message: string; code: string | null } | null>(null);
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);
  // Bumped by the "Try again" control to re-run the load effect (Session AFG).
  const [reloadNonce, setReloadNonce] = useState(0);

  const load = useCallback((w: string | undefined) => {
    let live = true;
    setLoading(true);
    setError(null);
    loadTimesheet(w)
      .then((res) => {
        if (!live) return;
        setData(res.data);
        setStale(res.stale);
      })
      .catch((err: unknown) => {
        // Keep the server's own words. apiRequest already parses the body and carries both the
        // status and the message, and throwing them away is what turned "This account has no
        // engineer profile" into a generic failure.
        //
        // NB ApiError.code is the human MESSAGE when the body carried one (api.ts sets
        // `code = body.message`), so the machine-readable code has to come off the parsed body.
        if (!live) return;
        setError(
          err instanceof ApiError
            ? {
                // Only PROSE is shown. ApiError.code is the status text or a machine slug whenever
                // the body carried no `message`, and "forbidden" on screen is worse than the
                // generic line it would replace. See lib/serverMessage.ts.
                message: presentableServerMessage(err.message),
                code: typeof err.body?.error === "string" ? err.body.error : null,
                // A permissions refusal cannot be retried away, so nothing is offered to press --
                // the same rule the office Timesheets page follows.
                retryable: err.status !== 401 && err.status !== 403,
              }
            : { message: null, code: null, retryable: true },
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    // A new week (or a "Try again" tap) is a clean slate: drop any just-submitted stamp, error or
    // open confirm sheet.
    setSubmittedAt(null);
    setSubmitError(null);
    setConfirmOpen(false);
    // An oversight account has no sheet to load; the notice below stands in for it and no request
    // is made.
    if (!ownRecords) {
      setLoading(false);
      return;
    }
    return load(week);
  }, [load, week, reloadNonce, ownRecords]);

  async function submit() {
    if (!data?.timesheetId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.timesheets.submit(data.timesheetId);
      setData((prev) => (prev ? { ...prev, status: res.status } : prev));
      setSubmittedAt(new Date());
      // The cache still holds the pre-submit DRAFT sheet under a cachedAt timestamp that is still
      // well within its lifetime, so leaving it in place would let the NEXT visit to this tab serve
      // that now-superseded status back as if it were live, with no stale notice. Drop the cache so
      // the next read is always a genuine fetch. A failure here must never turn a submit that has
      // already succeeded into a reported failure, so it is swallowed.
      try {
        await clearTimesheetCache();
      } catch {
        // Best-effort only; the in-memory state above already reflects the true new status.
      }
    } catch (err) {
      setSubmitError({
        message: "Could not submit your timesheet just now. Please try again.",
        code: err instanceof ApiError ? err.code : null,
      });
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  }

  const currentStart = data?.weekStarting;
  const entries = data ? data.days.flatMap((d) => d.entries) : [];
  const pendingJobs = data?.pendingJobs ?? [];
  const unrecordedJobs = data?.unrecordedJobs ?? [];
  const hasEntries = entries.length > 0;
  const hasPending = pendingJobs.length > 0;
  const hasUnrecorded = unrecordedJobs.length > 0;
  const canSubmit = data?.status === "DRAFT" && !!data.timesheetId && hasEntries;

  // No engineer profile, so no timesheet of this account's own. Not a failure, and a retry could
  // never help, so it is stated rather than errored (Session AGV). The office roster is where every
  // engineer's timesheet lives, and an admin reaching the PWA can open it.
  if (!ownRecords || error?.code === "no_engineer_profile") {
    return (
      <OversightNotice
        title="My timesheet"
        explanation={
          isOversightAccount(user)
            ? "You are signed in with an office account, which has no timesheet of its own. Every engineer's timesheet is on the office Timesheets page."
            : "This account has no engineer profile, so it has no timesheet of its own."
        }
        officeHref={isOversightAccount(user) ? "/office/timesheets" : undefined}
        officeLabel={isOversightAccount(user) ? "Open office timesheets" : undefined}
      />
    );
  }

  if (error && !data) {
    // Be honest about WHY it failed. When the device is genuinely offline, say so and offer nothing
    // to press (a retry cannot reach the server). When the device is online, the load failed for
    // some other reason, so do NOT claim they are offline: say it plainly and give a Try again
    // (Session AFG). The previous copy always claimed "back online", which misled an online engineer
    // and the office into treating a server-side blip as a connectivity problem.
    //
    // Session AGV: show the server's OWN explanation when it sent one, instead of the generic line.
    // A refusal that names its reason is actionable; "could not load" behind a retry is not.
    return (
      <div>
        <h1 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">My timesheet</h1>
        <div role="alert" className="rounded-lg border border-ele-card-border bg-ele-card p-6 text-center">
          <p className="text-sm text-ele-strong">{error.message ?? "Could not load your timesheet just now."}</p>
          <p className="mt-1 text-sm text-ele-muted">
            {!online
              ? "It will appear when you are back online."
              : error.retryable
                ? "Please try again."
                : "Ask the office to check your account."}
          </p>
          {online && error.retryable && (
            <button
              type="button"
              onClick={() => setReloadNonce((n) => n + 1)}
              className="mt-4 inline-flex min-h-11 items-center rounded-lg border border-[#2CB9F3] px-4 font-menu text-xs tracking-wide text-[var(--ele-loler-ink)]"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">My timesheet</h1>
      <p className="mb-3 text-sm text-ele-muted">Your hours, from the visits you have completed. Your travel is recorded on each job sheet.</p>
      {stale && <StaleNotice />}

      {/* Week nav: steps back and forward; "This week" returns to the current week. */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous week"
          onClick={() => currentStart && setWeek(shiftWeek(currentStart, -1))}
          disabled={!currentStart}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-ele-card-border text-ele-muted transition hover:text-[var(--ele-green-ink)] disabled:opacity-40"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
        {week !== undefined ? (
          <button
            type="button"
            onClick={() => setWeek(undefined)}
            className="h-11 rounded-lg border border-[#63C41F] px-4 font-menu text-xs tracking-wide text-[var(--ele-green-ink)]"
          >
            This week
          </button>
        ) : (
          <span className="font-menu text-xs uppercase tracking-wider text-ele-muted">This week</span>
        )}
        <button
          type="button"
          aria-label="Next week"
          onClick={() => currentStart && setWeek(shiftWeek(currentStart, 1))}
          disabled={!currentStart}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-ele-card-border text-ele-muted transition hover:text-[var(--ele-green-ink)] disabled:opacity-40"
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {loading && !data ? (
        <p className="text-sm text-ele-muted">Loading...</p>
      ) : (
        data && (
          <>
            {/* Week header: the range, the week's total, and the current status. */}
            <div className="rounded-lg border border-ele-card-border bg-ele-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-menu text-xs uppercase tracking-wider text-ele-muted">Week</p>
                  <p className="mt-0.5 text-sm text-ele-strong">{data.weekLabel}</p>
                </div>
                <StatusPill status={data.status} />
              </div>
              <p className="mt-3 font-heading text-[36px] leading-none text-[var(--ele-green-ink)]">{formatDurationHm(data.totals.totalMins)}</p>
              <p className="mt-1 font-menu text-xs uppercase tracking-wider text-ele-muted">Total hours</p>
              {(data.byType?.length ?? 0) > 0 && <TimesheetBreakdown buckets={data.byType ?? []} className="mt-3" />}
            </div>

            {/* Entries and any pending (awaiting-approval) visits. */}
            {hasEntries || hasPending || hasUnrecorded ? (
              <div className="mt-4 space-y-4">
                {hasEntries && (
                  <ul className="overflow-hidden rounded-lg border border-ele-card-border bg-ele-card">
                    {entries.map((e) => (
                      <EntryRow key={e.id} entry={e} />
                    ))}
                  </ul>
                )}
                {hasPending && (
                  <div>
                    <p className="mb-2 px-1 font-menu text-xs uppercase tracking-wider text-ele-muted">Awaiting office approval</p>
                    <ul className="overflow-hidden rounded-lg border border-ele-card-border bg-ele-card">
                      {pendingJobs.map((j) => (
                        <PendingRow key={j.jobId} job={j} />
                      ))}
                    </ul>
                    <p className="mt-2 px-1 text-xs text-ele-muted">These visits join your timesheet when the office approves them.</p>
                  </div>
                )}
                {/* Kept visually and verbally APART from the pending list above, because the two
                    mean opposite things: those arrive on their own, these never will. The closing
                    line names the one thing the engineer can actually do about it, since the office
                    is the only party who can add the hours. */}
                {hasUnrecorded && (
                  <div>
                    {/* Worded to match the office panel exactly, and deliberately NOT the same
                        string as the per-row pill below it: two different things reading "No time
                        recorded" on one screen is how a reader stops distinguishing the section
                        from the row. */}
                    <p className="mb-2 px-1 font-menu text-xs uppercase tracking-wider text-[var(--ele-amber-text-deep)]">Hours not on this timesheet</p>
                    <ul className="overflow-hidden rounded-lg border border-[#b8860b] bg-ele-card">
                      {unrecordedJobs.map((j) => (
                        <UnrecordedRow key={j.jobId} job={j} />
                      ))}
                    </ul>
                    <p className="mt-2 px-1 text-xs text-ele-muted">
                      {unrecordedJobs.length === 1 ? "This visit has" : "These visits have"} no usable arrival and departure time, so no hours reached
                      your timesheet. Tell the office and they will add them.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-4 rounded-lg border border-ele-card-border bg-ele-card p-6 text-center text-sm text-ele-muted">
                No completed jobs this week.
              </p>
            )}

            {/* Approved: the office sign-off (and any note it left). */}
            {data.status === "APPROVED" && (
              <div className="mt-5 rounded-lg border border-[#63C41F] bg-ele-card p-4">
                <p className="text-sm font-semibold text-[var(--ele-green-ink)]">
                  {`Approved${data.approvedAt ? ` ${fullDateLabel(data.approvedAt)}` : ""}${data.approvedByName ? ` by ${data.approvedByName}` : ""}`}
                </p>
                {data.notes && <p className="mt-2 text-sm text-ele-strong">{data.notes}</p>}
              </div>
            )}

            {/* Submitted (loaded already-submitted, or just now). */}
            {data.status === "SUBMITTED" && (
              <p className="mt-5 text-sm text-ele-muted">
                {submittedAt ? `Submitted ${dateTimeStamp(submittedAt)}. The office will review it.` : "Submitted. Awaiting office approval."}
              </p>
            )}

            {/* Submit for approval: a DRAFT week with a timesheet row and at least one entry. */}
            {canSubmit && (
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  disabled={!online || submitting}
                  className="flex min-h-12 w-full items-center justify-center rounded-lg bg-[#63C41F] px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg transition disabled:opacity-50"
                >
                  Submit timesheet
                </button>
                {!online && <p className="mt-2 text-xs text-ele-muted">You need a connection to submit.</p>}
                {/* Said, not enforced. Travel logging is the first thing that can put a timesheet
                    row on a week before any job is approved, so an engineer can now reach a live
                    Submit button while visits are still with the office. That is not wrong -- the
                    hours still land on the week afterwards, because auto-generation refuses only an
                    APPROVED week -- so this is a note rather than a refusal. The real guard is on
                    the office's Approve, which is where signing the week off would actually strand
                    anything. */}
                {hasPending && (
                  <p className="mt-2 text-xs text-[var(--ele-amber-text-deep)]">
                    {pendingJobs.length === 1 ? "1 visit is" : `${pendingJobs.length} visits are`} still with the office. They will be added to this
                    week when approved.
                  </p>
                )}
                {submitError && (
                  <p role="alert" className="mt-2 text-sm text-ele-danger">
                    {submitError.message}
                    {submitError.code && <span className="mt-0.5 block text-xs text-ele-muted">Reference: {submitError.code}</span>}
                  </p>
                )}
              </div>
            )}

            <p className="mt-6 text-xs text-ele-muted">If something here looks wrong, tell the office and they will correct it.</p>
          </>
        )
      )}

      {/* Bottom-sheet confirm: the branded two-step for a consequential action (a submit notifies
          the office). Backdrop or Cancel dismisses; Confirm submits. */}
      {confirmOpen && data && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="Confirm timesheet submission">
          <button type="button" aria-label="Cancel" className="flex-1 bg-black/60" onClick={() => !submitting && setConfirmOpen(false)} />
          <div className="rounded-t-2xl border-t border-ele-card-border bg-ele-card p-5 pb-8">
            <h2 className="font-heading text-lg text-ele-strong">Submit timesheet?</h2>
            <p className="mt-2 text-sm text-ele-muted">{`Submit your timesheet for ${data.weekLabel}? The office will be notified.`}</p>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting}
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-lg bg-[#63C41F] px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Confirm and submit"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              className="mt-2 flex min-h-11 w-full items-center justify-center text-sm text-ele-muted disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
